/**
 * Device detection service (no React hooks) for legacy components.
 * Reusable across the app to detect current device type and listen to changes.
 */

const DEFAULT_BREAKPOINTS = {
  mobileMax: 767,   // <= 767px considered mobile
  tabletMax: 1024,  // 768px - 1024px considered tablet
};

let activeBreakpoints = { ...DEFAULT_BREAKPOINTS };

/**
 * Optionally override the default breakpoints.
 * @param {{ mobileMax?: number, tabletMax?: number }} bp
 */
export function setBreakpoints(bp = {}) {
  if (typeof bp.mobileMax === 'number') activeBreakpoints.mobileMax = bp.mobileMax;
  if (typeof bp.tabletMax === 'number') activeBreakpoints.tabletMax = bp.tabletMax;
}

function hasWindow() {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined';
}

function getViewportWidth() {
  if (!hasWindow()) return 0;
  return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || 0;
}

function ua() {
  if (!hasWindow()) return '';
  return navigator.userAgent || navigator.vendor || (window.opera && window.opera.toString()) || '';
}

/**
 * Returns true if the device supports touch input.
 */
export function isTouchDevice() {
  if (!hasWindow()) return false;
  return (
    'ontouchstart' in window ||
    (navigator.maxTouchPoints || 0) > 0 ||
    (navigator.msMaxTouchPoints || 0) > 0
  );
}

/**
 * Heuristic: UA check for phones OR viewport width <= mobileMax
 */
export function isMobile() {
  if (!hasWindow()) return false;
  const width = getViewportWidth();
  const agent = ua();
  const isPhoneUA = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(agent) && !/iPad/i.test(agent);
  return isPhoneUA || width <= activeBreakpoints.mobileMax;
}

/**
 * Heuristic: UA check for tablets OR width between mobileMax+1 and tabletMax
 */
export function isTablet() {
  if (!hasWindow()) return false;
  const width = getViewportWidth();
  const agent = ua();
  const isTabletUA = /(iPad|Tablet|PlayBook)/i.test(agent) || (/Android/i.test(agent) && !/Mobile/i.test(agent));
  return isTabletUA || (width > activeBreakpoints.mobileMax && width <= activeBreakpoints.tabletMax);
}

export function isDesktop() {
  if (!hasWindow()) return true; // assume desktop on SSR
  const width = getViewportWidth();
  return !isMobile() && !isTablet() && width > activeBreakpoints.tabletMax;
}

/**
 * Get the current device type: 'mobile' | 'tablet' | 'desktop'
 */
export function getDeviceType() {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
}

/**
 * Subscribe to device type changes (on resize/orientation changes).
 * Returns an unsubscribe function.
 * @param {(type: 'mobile'|'tablet'|'desktop') => void} callback
 */
export function onDeviceTypeChange(callback) {
  if (typeof callback !== 'function') return () => {};
  let last = getDeviceType();

  const handler = () => {
    const current = getDeviceType();
    if (current !== last) {
      last = current;
      try { callback(current); } catch (_) {}
    }
  };

  if (hasWindow()) {
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('orientationchange', handler, { passive: true });
  }

  // initial async tick to ensure consumer sees the current value
  if (typeof setTimeout === 'function') {
    setTimeout(() => {
      try { callback(last); } catch (_) {}
    }, 0);
  }

  return function unsubscribe() {
    if (hasWindow()) {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    }
  };
}

export default {
  setBreakpoints,
  isTouchDevice,
  isMobile,
  isTablet,
  isDesktop,
  getDeviceType,
  onDeviceTypeChange,
};

